import { Fragment, useRef, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { recordSwitchedTo, useGlobalContext } from '@admin-panel/hooks';

/**
 * Every Hub page belongs to the working facility. When the worker changes facility:
 * - a record page (a route with a param, e.g. /packages/show/:id) opened under the old facility goes home, since the
 *   record may belong to the old facility and its actions must not run under the new one. A record page that switched
 *   the facility to its own record's (switchFacilityForRecord) stays open, started over under that facility;
 * - any other page starts over: its subtree is keyed by the facility, so forms and local state reset.
 */
export const FacilityScope = ({ children }: { children: ReactNode }) => {
  const { warehouseId } = useGlobalContext();
  const { pathname } = useLocation();
  const params = useParams();
  const openedUnder = useRef({ pathname, facility: warehouseId });
  if (openedUnder.current.pathname !== pathname || openedUnder.current.facility === undefined) {
    openedUnder.current = { pathname, facility: warehouseId };
  }

  const isRecordPage = Object.keys(params).some(key => key !== '*');
  if (
    isRecordPage &&
    openedUnder.current.facility !== warehouseId &&
    warehouseId !== undefined &&
    recordSwitchedTo(pathname) === warehouseId
  ) {
    openedUnder.current = { pathname, facility: warehouseId };
  }
  if (isRecordPage && openedUnder.current.facility !== warehouseId) {
    return <Navigate to="/" replace />;
  }
  return <Fragment key={String(warehouseId)}>{children}</Fragment>;
};
